import { computed, reactive, ref } from "vue"
import {
  ADMIN_FILTER_KEYS,
  buildFilterUrl,
  dispatchFilterChange,
  hasActiveAdvancedFilters,
  hasActiveFilters,
} from "../utils/admin_filters"

function emptyFilters(initial = {}) {
  return ADMIN_FILTER_KEYS.reduce((acc, key) => {
    acc[key] = initial[key] ?? ""
    return acc
  }, {})
}

export function useAdminFilters(options) {
  const filters = reactive(emptyFilters(options.initialFilters))
  const advancedOpen = ref(options.advancedExpanded || hasActiveAdvancedFilters(filters))
  const saveName = ref("")

  let debounceTimer = null

  const showClear = computed(() => hasActiveFilters(filters))
  const showSaveSearch = computed(() => hasActiveFilters(filters))
  const advancedToggleLabel = computed(() =>
    advancedOpen.value
      ? `▲ ${options.labels?.advanced || "Advanced"}`
      : `▼ ${options.labels?.advanced || "Advanced"}`
  )

  function applyFilters() {
    const url = buildFilterUrl(options.url, filters)
    dispatchFilterChange(url, { ...filters })
  }

  function debouncedSearch() {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(applyFilters, options.debounceMs || 300)
  }

  function onSelectChange() {
    applyFilters()
  }

  function clearFilters(event) {
    event?.preventDefault()
    Object.assign(filters, emptyFilters())
    applyFilters()
  }

  function toggleAdvanced() {
    advancedOpen.value = !advancedOpen.value
  }

  async function saveSearch(event) {
    event?.preventDefault()
    const name = saveName.value.trim()
    if (!name) return

    const query = {}
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query[key] = value
    })

    const response = await fetch(options.savedSearchesUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": options.csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify({
        saved_search: { name, query },
      }),
    })

    if (response.ok) {
      window.location.reload()
    } else {
      alert(options.labels?.saveFailed || "Failed to save search")
    }
  }

  function cleanup() {
    clearTimeout(debounceTimer)
  }

  return {
    filters,
    advancedOpen,
    saveName,
    showClear,
    showSaveSearch,
    advancedToggleLabel,
    applyFilters,
    debouncedSearch,
    onSelectChange,
    clearFilters,
    toggleAdvanced,
    saveSearch,
    cleanup,
  }
}
